export type QuestionOption = { label: string; hint?: string; default?: boolean }

export type ReadableQuestion = {
  question: string
  why?: string
  details?: string
  options: QuestionOption[]
}

type Raw = { question: string; why?: string; details?: string; options?: unknown }

const FALLBACK_QUESTION = "How would you like to proceed?"

/** "OIO" is the product's own shorthand; the user reads "order". */
function plain(text: string | undefined): string | undefined {
  if (!text) return text
  return text.replace(/\bOIO\b/g, "order").replace(/\s+/g, " ").trim()
}

function structured(options: unknown): QuestionOption[] {
  if (!Array.isArray(options)) return []
  const rows: QuestionOption[] = []
  for (const o of options) {
    if (typeof o === "string" && o.trim()) rows.push({ label: o.trim() })
    else if (o && typeof o === "object" && typeof (o as QuestionOption).label === "string" && (o as QuestionOption).label.trim()) {
      const { label, hint, default: d } = o as QuestionOption
      rows.push({ label: label.trim(), ...(hint ? { hint } : {}), ...(d ? { default: true } : {}) })
    }
  }
  return rows
}

/** "Revise specific sections (specify)" -> label plus a hint, unless the bracket is only "specify". */
function toOption(text: string): QuestionOption | null {
  const cleaned = text.replace(/^[\s;,.]+|[\s;,.]+$/g, "").replace(/^or\s+/i, "").trim()
  if (!cleaned) return null
  const m = cleaned.match(/^(.*\S)\s*\(([^()]+)\)$/)
  if (!m) return { label: cleaned }
  const hint = m[2].trim()
  if (/^(please )?specify$/i.test(hint)) return { label: m[1].trim() }
  return { label: m[1].trim(), hint }
}

/**
 * A question string an older agent wrote as one blob, "Options: (1) A; (2) B; (3) Other (default: (1) A)",
 * becomes a question plus choices. Anything that does not match is returned as it came (numbering stripped).
 */
export function parseLegacyQuestion(text: string): { question: string; options: QuestionOption[] } {
  const stripped = text.replace(/^\s*\d+[.)]\s+(?!.*\n\s*\d+[.)]\s)/s, "").trim()
  const at = stripped.search(/\boptions?\s*:/i)
  if (at < 0) return { question: stripped, options: [] }
  const lead = stripped.slice(0, at).replace(/[\s:;,.-]+$/, "").trim()
  let body = stripped.slice(at).replace(/^\boptions?\s*:\s*/i, "")
  let defaultText = ""
  const d = body.search(/\(\s*default\s*[:=]/i)
  if (d >= 0) {
    defaultText = body.slice(d).replace(/^\(\s*default\s*[:=]\s*/i, "").replace(/\)\s*\.?\s*$/, "").trim()
    body = body.slice(0, d)
  }
  const parts = body.split(/\(\s*(\d+)\s*\)/)
  // split with a capture group: [before, "1", textA, "2", textB, ...]
  const numbered: { n: number; option: QuestionOption }[] = []
  for (let i = 1; i + 1 < parts.length; i += 2) {
    const option = toOption(parts[i + 1])
    if (option) numbered.push({ n: Number(parts[i]), option })
  }
  if (numbered.length < 2) return { question: stripped, options: [] }
  const dn = defaultText.match(/^\(\s*(\d+)\s*\)/)
  const dLabel = defaultText.replace(/^\(\s*\d+\s*\)\s*/, "").trim().toLowerCase()
  const options: QuestionOption[] = []
  for (const { n, option } of numbered) {
    // A bare "Other" is the free-text box, which the card always offers.
    if (/^other\b/i.test(option.label) && !option.hint) continue
    const isDefault = dn ? Number(dn[1]) === n : !!dLabel && option.label.toLowerCase().startsWith(dLabel)
    options.push(isDefault ? { ...option, default: true } : option)
  }
  if (options.length < 2) return { question: stripped, options: [] }
  return { question: lead || FALLBACK_QUESTION, options }
}

/** What the card shows: a plain question, optional reason, and choices (from the payload, or parsed from an old blob). */
export function readableQuestion(payload: Raw): ReadableQuestion {
  let options = structured(payload.options)
  let question = payload.question
  if (options.length === 0) {
    const parsed = parseLegacyQuestion(payload.question)
    question = parsed.question
    options = parsed.options
  } else {
    question = parseLegacyQuestion(payload.question).question
  }
  // At most one recommended choice.
  let seen = false
  options = options.map((o) => (o.default && !seen ? ((seen = true), o) : { ...o, default: false }))
  return { question: plain(question) ?? FALLBACK_QUESTION, why: plain(payload.why), details: payload.details, options }
}
